import LiteTradeChartWrapper from "../../../modules/LiteTrade/components/LiteTradeChart/LiteTradeChartWrapper.jsx";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { createStructuredSelector } from "reselect";
import {
    getLiteTradeAccountPortfolio,
    makeSelectLiteTradeMarketDataBySymbol,
    makeSelectLiteTradeMarketDataCandleBySymbol,
    makeSelectorGetAssetInstance
} from "../../../modules/LiteTrade/liteTradeSelectors.js";
import { connect } from "react-redux";
import { Loader } from "../../../components/Loader/Loader.jsx";
import { MobileMenuFixedStyled, MobileMenuStyled } from "../styled/MobileMenuStyled.js";
import {
    MobileAssetInfoOverviewAmountStyled,
    MobileAssetInfoOverviewChartStyled,
    MobileAssetInfoOverviewStyled,
    MobileAssetInfoSellBuyMenuStyled, MobileAssetInfoSellBuyUnavailableStyled
} from "./styled/MobileAssetInfoOverviewStyled.js";
import LiteTradePortfolio from "../../../modules/LiteTrade/components/LiteTradePortfolio/LiteTradePortfolio.jsx";
import { MOBILE_SLIDER_PAGES_IDS } from "../MobileSliderPages/mobileSliderPagesConstants.js";
import { openMobileSliderPageAction } from "../../mobileAppActions.js";
import { useLangContext } from "../../../providers/LangProvider/LangProviderContext";
import withDebounced from "../../../hooks/withDebounced.js";
import {
    addMarketDataCandleWatchAction,
    addMarketDataWatchAction,
    removeMarketDataCandleWatchAction,
    removeMarketDataWatchAction
} from "../../../modules/LiteTrade/liteTradeActions.js";
import classNames from "classnames";
import { BinIcon } from "../../../components/icon/Icon.jsx";

function MobileAssetInfoOverview(props) {
    const {
        symbol,

        assetInstance,
        marketDataSymbol,
        marketDataCandleSymbol,
        openMobileSliderPage,
        addMarketDataWatch,
        removeMarketDataWatch,
        addMarketDataCandleWatch,
        removeMarketDataCandleWatch,
    } = props;

    const {getLangPath} = useLangContext();

    useEffect(() => {
        addMarketDataWatch({fromComponentName: 'asset_info', symbol});
        addMarketDataCandleWatch({fromComponentName: 'asset_info', symbol});
        return () => {
            removeMarketDataWatch({fromComponentName: 'asset_info'});
            removeMarketDataCandleWatch({fromComponentName: 'asset_info'});
        }
    }, [addMarketDataCandleWatch, addMarketDataWatch, removeMarketDataCandleWatch, removeMarketDataWatch, symbol]);

    const handlePositionCreate = useCallback((e) => {
        e.preventDefault();
        openMobileSliderPage({
            name: MOBILE_SLIDER_PAGES_IDS.POSITION_CREATE,
            side: e.currentTarget.dataset.side,
            symbol,
        });
    }, [openMobileSliderPage, symbol]);

    const diff = useMemo(() => {
        if (!marketDataCandleSymbol) {
            return {};
        }
        const keys = Object.keys(marketDataCandleSymbol).sort();
        const firstCandleTime = keys[keys.length - 2];
        const firstCandle = marketDataCandleSymbol[firstCandleTime];
        const lastCandleTime = keys[keys.length - 1];
        const lastCandle = marketDataCandleSymbol[lastCandleTime];
        if (!lastCandle || isNaN(lastCandle.close) || !firstCandle || isNaN(firstCandle.close)) {
            return {};
        }
        const amount = lastCandle.close - firstCandle.close;
        const percent = amount / firstCandle.close * 100;
        return {
            amount,
            percent,
        };
    }, [marketDataCandleSymbol]);

    const lastPrice = useMemo(() => {
        return marketDataSymbol?.bid;
    }, [marketDataSymbol]);

    if (!assetInstance) {
        return <Loader show={true}/>
    }

    return <MobileAssetInfoOverviewStyled>
        <MobileAssetInfoOverviewAmountStyled>
            {!!lastPrice ? assetInstance.amountToFixed(lastPrice) : '-'}
            {
                !!lastPrice &&
                <span className={classNames('diff', {
                    'positive': diff.amount > 0,
                    'negative': diff.amount < 0,
                })}>
                {!!diff.amount ? assetInstance.amountToFixed(diff.amount, true) : '-'}
                    {!!diff.percent && <>
                        <BinIcon style={{fontSize: '1.25em'}}
                                 name={diff.amount > 0 ? 'IcGrowth' : 'IcReduction'}/> {Math.abs(diff.percent).toFixed(2)}%
                    </>}
            </span>
            }
        </MobileAssetInfoOverviewAmountStyled>
        <MobileAssetInfoOverviewChartStyled>
            <LiteTradeChartWrapper symbol={assetInstance.symbol}
                                   decimal={assetInstance.getAmountDecimals()}/>
        </MobileAssetInfoOverviewChartStyled>

        <LiteTradePortfolio symbol={assetInstance.symbol}/>

        <MobileMenuFixedStyled>
            <MobileMenuStyled>
                {
                    !!assetInstance.isTradingNow() ? <MobileAssetInfoSellBuyMenuStyled>
                            <div className={'sell'} data-side={'sell'}
                                 onClick={handlePositionCreate}>{getLangPath('lite_version.order.sell')}</div>
                            <div className={'buy'} data-side={'buy'}
                                 onClick={handlePositionCreate}>{getLangPath('lite_version.order.buy')}</div>
                        </MobileAssetInfoSellBuyMenuStyled>
                        : <>
                            <MobileAssetInfoSellBuyUnavailableStyled>
                                {getLangPath('lite_version.portfolio.inactive_by_schedule')}
                            </MobileAssetInfoSellBuyUnavailableStyled>
                        </>
                }
            </MobileMenuStyled>
        </MobileMenuFixedStyled>
    </MobileAssetInfoOverviewStyled>
}

const mapStateToProps = createStructuredSelector({
    assetInstance: (state, props) => makeSelectorGetAssetInstance(props.symbol)(state, props),
    marketDataSymbol: (state, props) => makeSelectLiteTradeMarketDataBySymbol(props.symbol)(state, props),
    marketDataCandleSymbol: (state, props) => makeSelectLiteTradeMarketDataCandleBySymbol(props.symbol)(state, props),
    portfolio: getLiteTradeAccountPortfolio,
});

const mapDispatchToProps = (dispatch) => ({
    addMarketDataWatch: payload => dispatch(addMarketDataWatchAction(payload)),
    addMarketDataCandleWatch: payload => dispatch(addMarketDataCandleWatchAction(payload)),
    removeMarketDataWatch: payload => dispatch(removeMarketDataWatchAction(payload)),
    removeMarketDataCandleWatch: payload => dispatch(removeMarketDataCandleWatchAction(payload)),
    openMobileSliderPage: payload => dispatch(openMobileSliderPageAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withDebounced({
    debounce: 1000,
    propNames: [
        'marketDataSymbol',
        'marketDataCandleSymbol'
    ],
})(memo(MobileAssetInfoOverview)));
